import { useAppDispatch } from '@/hooks'
import { useNavigation } from '@/app/hooks'
import { useInProgressUnenrollmenDevices } from '../hooks/useInProgressUnenrollmentDevices'
import { useState } from 'react'
import { UtilityProgram } from '@/types/utilityProgram'
import { MultiselectOption } from '@/app/components/Multiselect'
import {
  InProgressUnenrollmentDeviceType,
  InProgressUtilityProgramUnenrollment,
  setUnenrollmentReason,
  SetUnenrollmentReasonPayload,
} from '@/reducers/inProgressUtilityProgramUnenrollments'
import { logMessage } from '@/logging/sentry'
import useUtilityProgramRewards from '@/app/hooks/useUtilityProgramRewards'
import { useFeatureFlag } from '@/hooks/useFeatureFlag'
import { getDeviceSelectionForSuggestionPresentation } from '../utils'

function hasSuggestionsForReasons(
  deviceSelection: InProgressUtilityProgramUnenrollment['deviceSelection'],
  selectedReasons: SetUnenrollmentReasonPayload['reasons'],
  utilityProgram: UtilityProgram,
): boolean {
  const deviceSelectionForPresentation =
    getDeviceSelectionForSuggestionPresentation(
      deviceSelection,
      selectedReasons,
      utilityProgram,
    )

  return deviceSelectionForPresentation.length > 0
}

export default function useViewModel(props: {
  inProgressUnenrollment: InProgressUtilityProgramUnenrollment
  utilityProgram: UtilityProgram
}) {
  const navigate = useNavigation()
  const dispatch = useAppDispatch()
  const suggestionsFeatureEnabled = useFeatureFlag({
    flag: 'unenroll_suggestions',
    experimentalValue: true,
  })

  const { vehicles, thermostats, chargers, energySystems, isLoadingOrIdle } =
    useInProgressUnenrollmenDevices(props.inProgressUnenrollment)

  const { incentivesExist } = useUtilityProgramRewards(props.utilityProgram.id)

  const [selectedOptions, setSelectedOptions] = useState<
    SetUnenrollmentReasonPayload['reasons'] | undefined
  >(undefined)
  const makeDeviceOnSelectInputChange =
    (deviceType: InProgressUnenrollmentDeviceType) =>
    (selectedValues: MultiselectOption[]) => {
      setSelectedOptions((prev) => {
        // get the existing reasons that aren't for this device type, we will keep these
        // in the selected options
        const prevWithoutThisDevice =
          prev?.filter((r) => r.deviceType !== deviceType) ?? []

        // get the new reasons for this device type, we are replacing the existing reasons
        // for this device with the new ones
        const newDeviceTypeReasons = selectedValues
          .filter(({ selected }) => selected)
          .map(({ value }) => {
            return {
              deviceType,
              reason: value,
            }
          })
        return [...prevWithoutThisDevice, ...newDeviceTypeReasons]
      })
    }
  const onSubmit = () => {
    if (!selectedOptions || selectedOptions.length === 0) {
      logMessage(
        'Tried to submit unenrollment reason without selecting a reason',
        {
          logToConsole: true,
        },
      )
      return
    }
    dispatch(
      setUnenrollmentReason({
        reasons: selectedOptions,
        utilityProgramId: props.utilityProgram.id,
      }),
    )

    // Check if we should show suggestions based on the selected reasons
    const shouldShowSuggestions =
      suggestionsFeatureEnabled &&
      hasSuggestionsForReasons(
        props.inProgressUnenrollment.deviceSelection,
        selectedOptions,
        props.utilityProgram,
      )

    if (shouldShowSuggestions) {
      navigate.push(
        `/utility-program/${props.utilityProgram.id}/unenroll/suggestions`,
      )
    } else {
      navigate.push(
        `/utility-program/${props.utilityProgram.id}/unenroll/feedback`,
      )
    }
  }

  return {
    vehicles,
    thermostats,
    chargers,
    energySystems,
    utilityProgram: props.utilityProgram,
    isLoadingOrIdle: isLoadingOrIdle,
    makeDeviceOnSelectInputChange,
    onSubmit,
    incentivesExist,
    selectedOptions,
  }
}
